/**
 * Schema-node context menu — mirrors the database-node menu but scoped to a
 * single schema (the `schema-container-folder` node shown when objects are
 * grouped by schema).
 *
 * Provides:
 *   (A) "Script new" submenu — Table / View / Stored Procedure / Function
 *       templates targeting THIS schema. Reuses the database-level handlers.
 *   (B) "Script schema as" submenu —
 *       1. CREATE / DROP SCHEMA (only for engines with first-class schema DDL).
 *       2. "Script all objects" — CREATE statements for every object in the
 *          schema, looping the existing single-object CREATE infra.
 */

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { TreeNode } from '../../explorer/models/TreeModel';
import { toast } from '../../../components/ui/toaster';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { getScriptProvider, type SchemaScriptType } from '../../scripting';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import type { DatabaseObject } from '../models/databaseObjectTypes';
import type { SchemaContainerFolderMetadata } from '../../explorer/models/nodeTypes';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

type SchemaNodeLike = TreeNode & { metadata?: Partial<SchemaContainerFolderMetadata> };

/**
 * Engines where `CREATE SCHEMA` is real, standard DDL. For the others a
 * "schema" is either a database (MySQL), a user (Oracle), or doesn't exist
 * (SQLite), so we gate CREATE/DROP SCHEMA off rather than emit misleading DDL.
 * Mock maps to MSSQL.
 */
export function engineSupportsSchemaDdl(engine?: string): boolean {
  const normalized = engine === 'mock' ? 'mssql' : engine;
  return normalized === 'mssql' || normalized === 'pgsql';
}

/**
 * Build the schema-scoped context menu actions.
 */
export function generateSchemaContextMenuActions(engine?: string): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  actions.push({ id: 'copy-name', label: 'Copy schema name', icon: 'clipboard' });

  // (A) Script new — schema-scoped templates. SQLite has no procedures/functions.
  const scriptNewSubmenu: ContextMenuAction[] = [
    { id: 'script-new-table', label: 'Table...', icon: 'table' },
    { id: 'script-new-view', label: 'View...', icon: 'file-text' },
  ];
  if (engine !== 'sqlite') {
    scriptNewSubmenu.push(
      { id: 'script-new-procedure', label: 'Stored Procedure...', icon: 'code' },
      { id: 'script-new-function', label: 'Function...', icon: 'code' },
    );
  }
  actions.push({ id: 'script-new', label: 'Script New', icon: 'file-plus', submenu: scriptNewSubmenu });

  // (B) Script schema as — CREATE/DROP (gated) + Script all objects.
  const scriptSchemaSubmenu: ContextMenuAction[] = [];
  if (engineSupportsSchemaDdl(engine)) {
    scriptSchemaSubmenu.push(
      { id: 'script-schema-create', label: 'CREATE', icon: 'file-plus' },
      { id: 'script-schema-drop', label: 'DROP', icon: 'trash' },
    );
  }
  scriptSchemaSubmenu.push({ id: 'script-schema-all-objects', label: 'Script all objects', icon: 'code' });
  actions.push({ id: 'script-schema-as', label: 'Script Schema As', icon: 'code', submenu: scriptSchemaSubmenu });

  actions.push({ id: 'separator', label: '', isSeparator: true });
  actions.push({ id: 'refresh', label: 'Refresh', icon: 'refresh' });

  return actions;
}

function trackSchemaEvent(
  name: string,
  props: Record<string, string | number | boolean | null>,
): void {
  window.electronAPI?.analytics?.track(name, props).catch(() => {
    // analytics is fire-and-forget
  });
}

/**
 * Handle Script schema as CREATE/DROP — mirrors handleScriptDatabaseAs.
 */
export function handleScriptSchemaAs(node: SchemaNodeLike, scriptType: SchemaScriptType): void {
  const connectionId = node.metadata?.connectionId;
  const schema = node.metadata?.schema ?? node.name;
  const database = node.metadata?.database ?? '';

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { connection, activeConnection, engine } = getConnectionById(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  if (!provider.generateSchemaScript) {
    toast.error({ title: 'Not supported', description: 'Schema DDL is not available for this engine.' });
    return;
  }

  try {
    const script = provider.generateSchemaScript(scriptType, { schema });
    const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
    const queryNumber = getNextQueryNumber();
    addTab({
      title: `${schema}_${scriptType === 'create' ? 'CREATE' : 'DROP'}-${queryNumber}`,
      type: 'query',
      content: { query: script },
      connectionInfo: {
        connectionId,
        connectionName: safeConnectionName(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection?.connection),
        database,
      },
    });
    trackSchemaEvent('feature:script_schema_as', { type: scriptType, source: 'schema_context_menu' });
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate script',
    });
  }
}

/**
 * Collect the DatabaseObjects in this schema by walking the schema-container
 * node's descendants (Tables / Views / Programmability folders are
 * pre-populated when grouping is active).
 */
function collectSchemaObjects(node: TreeNode): DatabaseObject[] {
  const objects: DatabaseObject[] = [];
  const visit = (n: TreeNode) => {
    const details = (n.metadata as { objectDetails?: DatabaseObject } | undefined)?.objectDetails;
    if (details && (details.type === 'table' || details.type === 'view' || details.type === 'stored-procedure' || details.type === 'function')) {
      objects.push(details);
    }
    for (const child of n.children ?? []) visit(child);
  };
  for (const child of node.children ?? []) visit(child);
  return objects;
}

/** Order CREATE statements sensibly: tables, then views, then procs, then functions. */
const TYPE_ORDER: Record<string, number> = {
  table: 0,
  view: 1,
  'stored-procedure': 2,
  function: 3,
};

/**
 * Generate a CREATE script string for one object, reusing the single-object
 * scripting infra. Resilient — returns a comment on failure rather than throwing.
 */
async function buildCreateScriptForObject(
  obj: DatabaseObject,
  connectionId: string,
  database: string,
  engine: string,
): Promise<string> {
  try {
    if (obj.type === 'table') {
      let metadata: import('../../scripting').TableMetadata | undefined;
      if (window.electronAPI?.getTableMetadata) {
        const result = await window.electronAPI.getTableMetadata({
          connectionId,
          database,
          schema: obj.schema,
          tableName: obj.name,
        });
        if (result.success && result.metadata) metadata = result.metadata;
      }
      const provider = getScriptProvider(engine);
      return provider.generateTableScript('create', {
        database,
        schema: obj.schema,
        tableName: obj.name,
        metadata,
      });
    }

    const { fetchObjectDefinitionScript } = await import('./objectDefinitionService');
    const objectType = obj.type === 'stored-procedure' ? 'stored-procedure' : obj.type === 'function' ? 'function' : 'view';
    const { script, error } = await fetchObjectDefinitionScript({
      connectionId,
      database,
      engine,
      objectType,
      schema: obj.schema,
      name: obj.name,
      objectId: obj.id,
      mode: 'create',
    });
    if (error) {
      return `-- Skipped ${obj.schema}.${obj.name}: ${error}`;
    }
    return script;
  } catch (error) {
    return `-- Skipped ${obj.schema}.${obj.name}: ${error instanceof Error ? error.message : 'Unknown error'}`;
  }
}

/**
 * Handle "Script all objects" — concatenate each object's CREATE script into a
 * single new query tab. Sequential + resilient (a failing object becomes a
 * comment, never aborts the batch).
 */
export function handleScriptAllObjects(node: SchemaNodeLike): void {
  const connectionId = node.metadata?.connectionId;
  const schema = node.metadata?.schema ?? node.name;
  const database = node.metadata?.database ?? '';

  if (!connectionId) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { connection, activeConnection, engine } = getConnectionById(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const objects = collectSchemaObjects(node)
    .slice()
    .sort((a, b) => {
      const orderDelta = (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9);
      if (orderDelta !== 0) return orderDelta;
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

  if (objects.length === 0) {
    toast.default({
      title: 'No objects',
      description: `No scriptable objects found in ${schema}. Expand the schema first.`,
    });
    return;
  }

  // MSSQL/mock use GO batch separators; the others terminate per statement.
  const useGoSeparator = engine === 'mssql' || engine === 'mock';
  const separator = useGoSeparator ? '\nGO\n\n' : '\n\n';

  const { addTab, getNextQueryNumber, updateTabContent } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const tabId = addTab({
    title: `${schema}_AllObjects-${queryNumber}`,
    type: 'query',
    content: {
      query: `-- Generating CREATE scripts for ${objects.length} object(s) in ${schema}...\n`,
      isGeneratingScript: true,
    },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionName(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection?.connection),
      database,
    },
  });

  void (async () => {
    const parts: string[] = [];
    for (const obj of objects) {
      const script = await buildCreateScriptForObject(obj, connectionId, database, engine);
      parts.push(`-- ${obj.type.toUpperCase()}: ${obj.schema}.${obj.name}\n${script.trim()}`);
    }
    updateTabContent(tabId, {
      query: parts.join(separator) + (useGoSeparator ? '\nGO\n' : '\n'),
      isGeneratingScript: false,
    });
    trackSchemaEvent('feature:script_schema_all_objects', { source: 'schema_context_menu' });
  })();
}
